/* The canvas — `Builder planu - tydzien - programator.dc.html`'s `v.grid`, transcribed:
 *
 *   flex:1; min-height:0; display:flex; align-items:stretch; gap:10px;
 *   padding:14px 20px 16px; overflow-x:auto; overflow-y:hidden;
 *   animation:(swapA|swapB) .32s cubic-bezier(.2,.8,.3,1) both
 *
 * ── Two permitted departures, both ordered by the owner ─────────────────────────────────────
 * · **Training columns may shrink.** The mock pins `flex-shrink: 0`, so seven trainings at a 232px
 *   basis overflow. `KANON - builder planow` says the opposite ("tydzień zawsze mieści się na
 *   ekranie") and the owner settled it that way on 7 September 2026. See `DayColumn.module.css`.
 * · **The week keeps trailing room while the day panel is open**, so a column standing under the
 *   574px panel can be scrolled clear of it. It is grown OUT OF FLOW — see `[data-panel-room]` —
 *   so not one column changes width when the panel opens.
 *
 * ── The vertical scroll fade is GONE, and so is the wrapper that existed to hold it ─────────
 * `.canvas` was a non-scrolling positioning context around the scroller whose only job was to draw
 * a gradient over a clipped bottom edge. The mock draws no fade and sets `overflow-y: hidden` — a
 * day card's own body is what scrolls, which is why the mock's grid never needs to. Nothing the
 * mock does not draw may be visible (owner, 8 September 2026), so both are removed and the pane is
 * the mock's grid directly.
 *
 * ── It is still the phase tabs' panel, and it still carries a heading ───────────────────────
 * `role="tabpanel"`, labelled by the selected tab. The visually hidden `h2` stops the document
 * outline jumping H1 → H3 and gives the canvas a name — "Wzorzec tygodnia" — which is what the tab
 * band switches between. The columns are a `list`, not a `grid`: each column is one item with one
 * card in it, and announcing a seven-column grid with one row would describe a structure that is
 * not there. */

.pane {
  display: flex;
  flex: 1;
  align-items: stretch;
  min-height: 0;
  overflow: auto hidden;
}

/* The mock's own row: `gap: 10px` and `padding: 14px 20px 16px`.
 *
 * **The padding is on THIS element and not on the scroller**, and that is load-bearing rather than
 * tidy: the trailing room below is an absolutely positioned pseudo-element at `left: 100%` of this
 * box, so it starts at this box's padding edge. Moved onto `.pane`, the room measured 554px instead
 * of the panel's own 574 — Chrome does not add a scroll container's end padding to `scrollWidth` —
 * and the covered column came 20px short of clearing the panel. Measured by
 * `e2e/viewport-fit.spec.ts`.
 *
 * `flex: 1 0 auto`, and the no-shrink half is load-bearing too: with `flex: 1` the row shrank to the
 * scroller and every card lost its bottom, so the load module was clipped away by the card's own
 * `overflow: hidden` with nothing to say so. */
.week {
  position: relative;
  display: flex;
  flex: 1 0 auto;
  gap: var(--space-10px);
  align-items: stretch;
  min-width: 0;
  padding: var(--space-14px) var(--space-20px) var(--space-4);
}

/* How wide the room is: what covers the end, the day panel, the Buddy panel, or both side by side
 * (owner, 7 October 2026). */
.week[data-panel-room="day"] {
  --panel-room: var(--w-builder-panel);
}

.week[data-panel-room="assistant"] {
  --panel-room: var(--w-buddy-panel);
}

.week[data-panel-room="both"] {
  --panel-room: calc(var(--w-builder-panel) + var(--w-buddy-panel));
}

/* Trailing room the width of what covers the end, out of flow, so a column under it can be
 * scrolled clear of it and not one column changes width when the panel opens. `1px` tall and
 * pointer-transparent: it exists to widen `scrollWidth` and nothing else. */
.week[data-panel-room]::after {
  position: absolute;
  top: 0;
  left: 100%;
  width: var(--panel-room);
  height: 1px;
  pointer-events: none;
  content: "";
}

/* ── The phase switch ─────────────────────────────────────────────────────────────────────────
 * The mock's `swapA` / `swapB`, its held 420ms tuning and the whole argument behind it moved to
 * `shared/ui/swapReplay.module.css` on 13 September 2026, when the sequence canvas became the second
 * surface replaying the same fade against the same `--dur-grid-flex` column settle. `WeekPane.tsx`
 * imports its two classes from there. */
